// Version history for the LAX Connection Log app.
// Referenced by: ChangelogModal, HamburgerMenu, LoginScreen
export const CHANGELOG = [
  {
    version: "4.3.131",
    date: "Nov 2026",
    changes: [
      { type: "improved", text: "Expeditor Mode sign: passenger names are one large column. When they don't all fit, they scroll slowly upward in a loop, like a departures board; short lists stay still. With Reduce Motion on, they can be swiped instead." },
      { type: "fix", text: "Expeditor Mode sign: the flight details scale to fit, so the flight number no longer overlaps the names and \"CONNECTING TO\", the airline and the destination aren't cut off or wrapped (seen on the unfolded iPhone Duo)." },
    ],
  },
  {
    version: "4.3.130",
    date: "Nov 2026",
    changes: [
      { type: "new", text: "Expeditor Mode: the gate sign fills in the TDT passenger names automatically, as first initial and last name (\"J. Monas\"), A–Z. Passengers sharing a last name show as one line, e.g. \"Johnson Party of 4\". The names can still be edited before showing the sign, and \"Use TDT names\" puts the TDT list back. Long lists shrink and split into two columns so every name fits." },
    ],
  },
  {
    version: "4.3.129",
    date: "Nov 2026",
    changes: [
      { type: "fix", text: "iPhone Duo: the header, menu button, + button and Mission Control no longer sit under the status bar on the right when unfolded in landscape." },
      { type: "fix", text: "iPhone Duo: in the side-by-side view (and held upright), cards use the phone layout, so origin airports aren't cut off and the connection time no longer runs into the outbound flight number." },
    ],
  },
  {
    version: "4.3.128",
    date: "Nov 2026",
    changes: [
      { type: "new", text: "iPhone Duo: unfolded in landscape, connections show as a list on the left and the selected connection's details on the right, with the gap between them on the fold. Tap a card to show it; every card action works from the detail side too. Unfolded upright, cards use the wider layout and pop-ups open centered. Folded, the app is unchanged. Also applies to the iPad app." },
      { type: "new", text: "Mission Control on the iPhone Duo (unfolded) and the iPad app: opens full screen inside the app, with a slimmer top bar on the smaller screen. In landscape on the Duo, the connections panel fits to the left of the fold and the map to the right. Folding the Duo closes it." },
      { type: "improved", text: "Sign-in says Touch ID instead of Face ID on devices with Touch ID, such as the iPhone Duo." },
    ],
  },
  {
    version: "4.3.127",
    date: "Oct 2026",
    changes: [
      { type: "new", text: "\"Expedite in TDT\" in the card's long-press menu, until the outbound departs. On the next TDT sync, all of the connection's passengers are expedited in TDT to the inbound carrier's expeditor (LH, LX, OS and SN to the LHG Duty Manager; carriers without their own expeditor to United), by email, the same way it's done by hand. The card shows \"Expedite queued\" until it's sent, then \"Tracking\", and a remark records it. If it couldn't be sent, the expanded card says why. A queued expedite can be cancelled from the same menu." },
      { type: "new", text: "Admins: \"TDT Expedites\" switch in the menu (Off / Dry Run / On), in its own section below Pause TDT Auto-Sync. Dry Run logs what would be sent to TDT without sending anything. Turning it On takes a second tap to confirm." },
    ],
  },
  {
    version: "4.3.126",
    date: "Oct 2026",
    changes: [
      { type: "improved", text: "Shift report: passenger names and bag tags are only printed for passengers who didn't make it (an outcome such as Rebooked or Overnight set on Partial Success / Unsuccessful connections). Everyone else is left off the report." },
    ],
  },
  {
    version: "4.3.125",
    date: "Oct 2026",
    changes: [
      { type: "new", text: "Named passengers are grouped by the cabin they were booked in (First, Business, Economy) and listed A–Z within each, on the card, in Edit → Details and in the shift report. The cabin comes from TDT automatically; passengers added by hand are listed under Other." },
    ],
  },
  {
    version: "4.3.124",
    date: "Oct 2026",
    changes: [
      { type: "new", text: "Passengers and bag tags now fill in automatically from TDT on each sync (names and bag tags; TDT has no PNR). Anything typed in by hand is kept." },
      { type: "new", text: "Copy buttons next to each bag tag number and PNR on the expanded card." },
      { type: "improved", text: "Passenger details: PNR takes up to 6 letters/digits and bag tags up to 10 digits. A bag tag becomes a chip as soon as all 10 digits are in." },
      { type: "new", text: "Partial Success / Unsuccessful connections: set an outcome for each named passenger who didn't make it (Rebooked, Overnight, No-show, Refund, Other), with the flight they were rebooked onto and a note. Entered on the Edit screen; printed under the passenger in the shift report." },
      { type: "fixed", text: "Mission Control no longer shows a landed inbound flying away from LAX on its thru-flight's next leg (e.g. UA2039 continuing to PIT). Once a connection has an ATA, its inbound is taken off the map." },
    ],
  },
  {
    version: "4.3.123",
    date: "Oct 2026",
    changes: [
      { type: "new", text: "Export Shift Report lists each connection's named passengers with their PNRs and bag tag numbers, under the connection row." },
    ],
  },
  {
    version: "4.3.122",
    date: "Oct 2026",
    changes: [
      { type: "fixed", text: "Air Canada Rouge flights (e.g. AC1742) and Air Canada Express flights flown by Jazz (AC 8000s) now show live on Mission Control. They broadcast as ROU1742 / JZA8xxx rather than ACA, so they were never found." },
    ],
  },
  {
    version: "4.3.121",
    date: "Oct 2026",
    changes: [
      { type: "new", text: "Mission Control: select a connection to Edit (or Open), add a Remark or Message the partner without leaving the map." },
      { type: "new", text: "Mission Control timeline: the next 3 hours of connections as arrival → departure bars, coloured by risk, with a \"now\" line. Hover or click a bar to find the flight on the map." },
      { type: "new", text: "Mission Control situation alerts: flags flights that are holding, going around (below 3,000 ft near LAX, then climbing more than 1,000 ft) or diverting, on the card, the plane and in the top bar." },
      { type: "new", text: "Mission Control TV mode for an ops-room screen: larger type, keeps the screen awake, and cycles every 12 seconds through flights that need attention." },
      { type: "new", text: "Mission Control LAX conditions: weather (VFR/IFR, wind, visibility, ceiling) in the top bar, and a banner for FAA ground stops, ground delay programs and arrival delays at LAX." },
    ],
  },
  {
    version: "4.3.120",
    date: "Oct 2026",
    changes: [
      { type: "new", text: "Passenger details: add names, PNRs and bag tags (as many bags as needed) to a connection from the edit screen. They show in the expanded card, with a count (e.g. \"3 NAMED\") on the card. Visible to UA and the airlines on that connection." },
      { type: "new", text: "Search box in the filter bar: find a connection by flight number, airport or gate (e.g. \"1169\", \"YUL\", \"77\"). Searches across Open, Pending and Complete." },
      { type: "new", text: "Gate and time changes: when a gate changes or an ETA, ETD or STD moves 15+ minutes on an open card, it shows in amber on the card for 30 minutes (e.g. \"GATE 74→72\", \"ETA +25\")." },
      { type: "new", text: "TDT sync status under \"United Airlines\" in the header: when it last synced, amber if it's gone quiet, red if it failed (e.g. TDT max sessions), so you know when to use Import Excel." },
      { type: "fixed", text: "\"Pause TDT Auto-Sync\" now actually pauses the TDT sync." },
      { type: "improved", text: "\"Tracking\" now sits under the connection time, and is set automatically by the TDT sync; the manual toggles are gone from the menu and edit screen." },
      { type: "improved", text: "Connections are kept for 3 days (today, yesterday and the day before); Change Date offers those days. Fixed the evening cleanup deleting one day too many." },
    ],
  },
  {
    version: "4.3.119",
    date: "Oct 2026",
    changes: [
      { type: "improved", text: "New card layout. On wider screens the connection time sits large on the right (e.g. \"1h 9m\"), and one line along the bottom shows the status, Tracking in TDT, pax and bags, and the latest remark." },
      { type: "new", text: "Cards show how late a flight is next to its time (e.g. \"07:21 ATA +86\"), and a \"Departs in …\" countdown in the last hour before departure (amber in the last 15 minutes)." },
    ],
  },
  {
    version: "4.3.118",
    date: "Oct 2026",
    changes: [
      { type: "improved", text: "Cleaner cards: the status, Overdue and connection time are plain coloured text instead of boxed pills, with no dots or icons. The colours still mean the same thing." },
      { type: "improved", text: "\"Tracking in TDT\" is now a quiet grey label. It sits under Overdue on overdue cards and is hidden on completed cards." },
    ],
  },
  {
    version: "4.3.117",
    date: "Oct 2026",
    changes: [
      { type: "improved", text: "\"Tracking in TDT\" on the card is more subtle: a small amber label instead of a boxed badge with a dot." },
    ],
  },
  {
    version: "4.3.116",
    date: "Oct 2026",
    changes: [
      { type: "fixed", text: "Import Excel works with the current TDT export again. It was reading the wrong columns (e.g. the status from the ATD column), so it found no At Risk rows." },
      { type: "improved", text: "Import Excel now follows the same rules as the TDT sync: connections with expedited pax are imported and marked Tracking in TDT, finished expedites go to the Pending tab with pax expedited filled in, and ATA/ATD are picked up. The preview shows a TDT column." },
    ],
  },
  {
    version: "4.3.115",
    date: "Oct 2026",
    changes: [
      { type: "improved", text: "TDT-detected outcomes now fill in pax expedited from TDT's success count (e.g. Partial Success with 3 of 4 made it → 3 expedited). The Pending strip shows the count, and approving keeps it. A count already entered by an SCCO is never overwritten." },
    ],
  },
  {
    version: "4.3.114",
    date: "Oct 2026",
    changes: [
      { type: "new", text: "Pending tab (Open | Pending | Complete). When the TDT sync detects an expedite outcome (Success, Partial Success or Unsuccessful), the card moves to Pending with the detected result. A UA SCCO approves it to set the status and move it to Complete, or rejects it to send it back to Open." },
      { type: "improved", text: "Approving or rejecting adds a remark with who and when. A rejected result isn't suggested again unless TDT's outcome changes." },
    ],
  },
  {
    version: "4.3.113",
    date: "Oct 2026",
    changes: [
      { type: "improved", text: "The Monitoring badge now reads \"Tracking in TDT\" and is a little smaller. The toggle in the edit screen and long-press menu, Messages previews, and the shift export use the same wording." },
    ],
  },
  {
    version: "4.3.112",
    date: "Oct 2026",
    changes: [
      { type: "fixed", text: "Mission Control shows the correct aircraft type more often (e.g. AC791 showed \"AS50\", a helicopter, instead of A220-300). Types now come from a more accurate source, and unrecognised types are hidden rather than shown as raw codes." },
    ],
  },
  {
    version: "4.3.111",
    date: "Oct 2026",
    changes: [
      { type: "improved", text: "Live aircraft labels show the aircraft type on its own line under the flight number, with altitude and flight phase on the line below." },
    ],
  },
  {
    version: "4.3.110",
    date: "Oct 2026",
    changes: [
      { type: "improved", text: "Live aircraft labels in Mission Control now show altitude with the flight phase, e.g. \"Cruising at 34,000 feet\", \"Climbing through 12,300 feet\" or \"Descending through 8,000 feet\"." },
    ],
  },
  {
    version: "4.3.109",
    date: "Oct 2026",
    changes: [
      { type: "fixed", text: "Mission Control no longer shows a flight at another aircraft's position (e.g. NZ6 from Auckland appearing over Europe). Aircraft looked up by transponder ID are now matched to the right flight, and any position that isn't plausibly on the flight's route to LAX is ignored." },
    ],
  },
  {
    version: "4.3.108",
    date: "Oct 2026",
    changes: [
      { type: "fixed", text: "Mission Control live flight tracking is far more reliable — aircraft no longer drop off the map or go missing because the ADS-B feeds were being rate-limited." },
      { type: "improved", text: "Mission Control now tracks flights worldwide, including over Asia and Europe, and keeps a plane on the map through brief gaps in receiver coverage." },
      { type: "improved", text: "When a flight drops out of ADS-B coverage (e.g. over the Pacific), Mission Control keeps estimating its position from where it was last seen and how fast it was going, labelled \"Estimated · last seen Xm ago\"." },
      { type: "new", text: "Mission Control map labels now show the aircraft type under the flight number (e.g. 777-300ER, A350-900)." },
    ],
  },
  {
    version: "4.3.107",
    date: "Oct 2026",
    changes: [
      { type: "new", text: "MONITORING flag for connections tracked in the Star Alliance TDT tool. Toggle it from the edit screen or the long-press menu. It works alongside any status (e.g. PROTECTED) and doesn't move the card to Complete." },
      { type: "improved", text: "Cards with no status no longer show a default \"Monitoring\" badge, and the list toggle now reads Open | Complete." },
    ],
  },
  {
    version: "4.3.106",
    date: "Aug 2026",
    changes: [
      { type: "improved", text: "Shift export remarks now show as a full-width line below each connection (who/when tag + message) instead of being crammed into a narrow, all-caps table column." },
      { type: "improved", text: "Divider lines between connections in the shift export are darker and easier to follow." },
    ],
  },
  {
    version: "4.3.105",
    date: "Aug 2026",
    changes: [
      { type: "fixed", text: "Mission Control and flight route maps load correctly again — CARTO now requires an API key for basemap tiles." },
    ],
  },
  {
    version: "4.3.104",
    date: "Jun 2026",
    changes: [
      { type: "fixed", text: "Signing out from the menu now correctly deregisters push notifications and closes any open Mission Control windows." },
      { type: "fixed", text: "Urgent message haptic now only fires once per message, not on every 15-second poll while the banner is showing." },
      { type: "fixed", text: "Repeated sign-out calls during session expiry no longer cause duplicate side effects." },
      { type: "fixed", text: "APNs token save failure is now detected rather than silently ignored." },
      { type: "fixed", text: "TDT sync no longer affects connection cards belonging to other SCCOs." },
    ],
  },
  {
    version: "4.3.103",
    date: "May 2026",
    changes: [
      { type: "fixed", text: "Re-adding a connection card with the same flight numbers as a recently deleted card now works correctly." },
    ],
  },
  {
    version: "4.3.102",
    date: "May 2026",
    changes: [
      { type: "improved", text: "Filter Connections sheet: swipe down to close, matching all other bottom sheets in the app." },
      { type: "improved", text: "Unhide Cards button moved to the top of the connection list (was at the bottom) and label shortened." },
      { type: "improved", text: "Filter Connections section labels are now larger and easier to read." },
    ],
  },
  {
    version: "4.3.101",
    date: "May 2026",
    changes: [
      { type: "improved", text: "Hide card: cards are now fully invisible once hidden, and 'Unhide hidden cards' at the bottom of the list restores all at once. Hide card menu item is now white like the other menu options." },
      { type: "fixed", text: "Filter Connections sheet now uses the correct app fonts throughout." },
    ],
  },
  {
    version: "4.3.100",
    date: "May 2026",
    changes: [
      { type: "new", text: "SCCOs can now hide individual connection cards via the long-press menu. Hidden cards are invisible to other SCCOs and auto-clear at the end of the shift. A 'N hidden · Show' link at the bottom of the list lets you peek or restore them." },
    ],
  },
  {
    version: "4.3.99",
    date: "May 2026",
    changes: [
      { type: "improved", text: "SCCOs can now add a remark directly from the long-press / right-click menu on any connection card, without opening the full edit form." },
    ],
  },
  {
    version: "4.3.98",
    date: "May 2026",
    changes: [
      { type: "improved", text: "Swipe down to close any modal on iPhone — a drag handle pill appears at the top of each sheet, and pulling it down 80px dismisses the modal." },
    ],
  },
  {
    version: "4.3.97",
    date: "May 2026",
    changes: [
      { type: "improved", text: "Mission Control: estimated aircraft positions now use a three-phase speed profile (climb, cruise, descent), improving accuracy for flights near departure or on approach." },
    ],
  },
  {
    version: "4.3.96",
    date: "May 2026",
    changes: [
      { type: "fixed", text: "Mission Control now appears in the menu when using the app as a PWA on a Windows desktop (Edge or Chrome)." },
      { type: "fixed", text: "Mission Control now keeps overdue connections visible until they are marked completed or offloaded." },
      { type: "fixed", text: "Push notification toggle now shows a clear message if notifications are blocked in your browser, instead of silently doing nothing." },
      { type: "improved", text: "Mission Control: new toggle button (top-left) hides or shows the connections panel to give a clear view of the map." },
    ],
  },
  {
    version: "4.3.95",
    date: "May 2026",
    changes: [
      { type: "improved", text: "Connection Map in shift report: city codes no longer overlap when multiple airports cluster in the same region." },
      { type: "improved", text: "Connection Map labels now have a subtle background halo so route lines don't obscure city codes." },
      { type: "improved", text: "LAX label moved below the center dot for cleaner map layout." },
    ],
  },
  {
    version: "4.3.94",
    date: "May 2026",
    changes: [
      { type: "improved", text: "Face ID is now more reliable — enrollment persists across sign-outs and session expiries, and prompts automatically on app open." },
      { type: "fixed", text: "Push notifications restored after iOS native app work inadvertently changed the bundle ID and entitlements." },
    ],
  },
  {
    version: "4.3.93",
    date: "May 2026",
    changes: [
      { type: "improved", text: "PDF export reverted to browser print dialog — opens a clean print-ready view in a new tab." },
    ],
  },
  {
    version: "4.3.90",
    date: "May 2026",
    changes: [
      { type: "improved", text: "Shift report Connection Map is now more compact." },
    ],
  },
  {
    version: "4.3.89",
    date: "May 2026",
    changes: [
      { type: "improved", text: "Connection Map in shift report cleaned up — removed reference rings and compass labels for a cleaner look." },
    ],
  },
  {
    version: "4.3.88",
    date: "May 2026",
    changes: [
      { type: "improved", text: "Shift report Connection Map redesigned as a radial (polar) layout — LAX at center, airports placed by direction with distance scaling, so nearby routes (SFO, SAN, PHX) are as readable as long-haul ones." },
      { type: "improved", text: "App icon badge now counts both unread messages and unopened new connection cards." },
      { type: "fixed", text: "Fixed Safari crash when app is left open for extended periods (bypassed Navigator Locks API which crashes in Safari 26.5 beta)." },
    ],
  },
  {
    version: "4.3.87",
    date: "May 2026",
    changes: [
      { type: "new", text: "Shift report now includes a Connection Map — an inline SVG world map showing all inbound/outbound routes color-coded by outcome (green = success, indigo = rebooked, blue = protected, red = unable)." },
      { type: "fixed", text: "App icon badge now clears immediately when leaving the app, not just on next open." },
    ],
  },
  {
    version: "4.3.86",
    date: "May 2026",
    changes: [
      { type: "new", text: "SCCO can now request passenger contact info (email/phone) when approving amenities — partner fills in a structured form and the contacts are sent back in the thread." },
      { type: "improved", text: "Amenity approval step now includes a 'Request passenger contact info' checkbox for seamless voucher delivery workflow." },
      { type: "fixed", text: "Removed pre-filled placeholder text from amenity request note field." },
    ],
  },
  {
    version: "4.3.85",
    date: "May 2026",
    changes: [
      { type: "new", text: "Partner airlines can now request hotel and/or meal amenities from United when the UA inbound flight is delayed — via the context menu or expanded card button." },
      { type: "new", text: "Amenity requests appear as structured messages in the thread; SCCO can approve or deny with a controllable/uncontrollable delay classification." },
      { type: "new", text: "Entry cards show a live amenity status badge (pending / approved / denied) once a request is submitted." },
    ],
  },
  {
    version: "4.3.84",
    date: "May 2026",
    changes: [
      { type: "fixed", text: "Message compose font size and family now match the sent message bubbles." },
      { type: "fixed", text: "Images attached to messages now display inline instead of as a filename link." },
      { type: "fixed", text: "Selecting the camera to attach a photo no longer force-quits the iOS app." },
    ],
  },
  {
    version: "4.3.83",
    date: "May 2026",
    changes: [
      { type: "fixed", text: "Cards with an ATD on the outbound flight now correctly show overdue (red) when the entry is still unresolved." },
      { type: "fixed", text: "Auto-sync no longer writes ATD as a raw string (e.g. 0735A) — it is now correctly formatted as HH:MM." },
    ],
  },
  {
    version: "4.3.82",
    date: "May 2026",
    changes: [
      { type: "improved", text: "Push notifications now include passenger count and connection time when available." },
      { type: "improved", text: "Remark edit and delete icons are now more visible." },
    ],
  },
  {
    version: "4.3.81",
    date: "May 2026",
    changes: [
      { type: "improved", text: "UA SCCOs can now edit their own remarks inline — click the pencil icon next to any remark you submitted." },
      { type: "fixed", text: "Origin, gate, and flight number fields in the entry modal are wider to prevent text clipping with the SA font." },
    ],
  },
  {
    version: "4.3.80",
    date: "May 2026",
    changes: [
      { type: "fixed", text: "Remark input no longer jumps the cursor to the end when typing or inserting text mid-sentence." },
    ],
  },
  {
    version: "4.3.79",
    date: "May 2026",
    changes: [
      { type: "fixed", text: "Mission Control popup now closes correctly when signing out from the main window." },
    ],
  },
  {
    version: "4.3.78",
    date: "May 2026",
    changes: [
      { type: "fixed", text: "Mission Control popup now reliably closes when the main window signs out." },
    ],
  },
  {
    version: "4.3.77",
    date: "May 2026",
    changes: [
      { type: "fixed", text: "Mission Control popup now follows the main window's session — signing out in either window closes both. The popup no longer has its own independent timeout." },
    ],
  },
  {
    version: "4.3.76",
    date: "May 2026",
    changes: [
      { type: "improved", text: "Inactivity timeout extended from 30 to 45 minutes. Hard logout extended from 8 to 10 hours." },
    ],
  },
  {
    version: "4.3.75",
    date: "May 2026",
    changes: [
      { type: "fixed", text: "App no longer freezes silently when the session expires — it now signs out and returns to the login screen automatically." },
    ],
  },
  {
    version: "4.3.74",
    date: "May 2026",
    changes: [
      { type: "fixed", text: "Login screen captcha widget removed — it was showing a broken white box since captcha is disabled in Supabase." },
    ],
  },
  {
    version: "4.3.73",
    date: "Apr 2026",
    changes: [
      { type: "improved", text: "Post-shift (outlook) entries on the export report are more readable — opacity raised from 62% to 82%." },
    ],
  },
  {
    version: "4.3.72",
    date: "Apr 2026",
    changes: [
      { type: "improved", text: "Message bubbles are larger and wider — text bumped to 15px with the SA font, bubbles widened from 78% to 88% of the thread width." },
    ],
  },
  {
    version: "4.3.71",
    date: "Apr 2026",
    changes: [
      { type: "improved", text: "Mission Control map city codes are larger and easier to read — origin/destination labels bumped from 10px to 13px, LAX label from 11px to 14px." },
    ],
  },
  {
    version: "4.3.70",
    date: "Apr 2026",
    changes: [
      { type: "improved", text: "Mission Control now uses the Star Alliance font (HelveticaNeueStd/Barlow) throughout — replacing IBM Plex Mono and plain Helvetica Neue for consistency with the rest of the app." },
    ],
  },
  {
    version: "4.3.69",
    date: "Apr 2026",
    changes: [
      { type: "improved", text: "Context menu items use the primary SA font at 16px / medium weight — more legible and brand-consistent." },
    ],
  },
  {
    version: "4.3.68",
    date: "Apr 2026",
    changes: [
      { type: "fixed", text: "Expeditor sign header no longer overlaps the iOS status bar — uses the device safe area inset for correct spacing on all iPhone models." },
    ],
  },
  {
    version: "4.3.67",
    date: "Apr 2026",
    changes: [
      { type: "improved", text: "Expeditor sign theme toggle is now smaller and requires a 1-second hold to activate, preventing accidental mode switches." },
    ],
  },
  {
    version: "4.3.66",
    date: "Apr 2026",
    changes: [
      { type: "improved", text: "Expeditor sign light mode now uses the official Star Alliance Connection Service light header image." },
    ],
  },
  {
    version: "4.3.65",
    date: "Apr 2026",
    changes: [
      { type: "improved", text: "Expeditor sign has a light/dark mode toggle in the lower-left corner — tap the sun or moon icon to switch. Sign defaults to dark mode." },
    ],
  },
  {
    version: "4.3.64",
    date: "Apr 2026",
    changes: [
      { type: "improved", text: "Connection cards on mobile now stack inbound and outbound vertically — full route info (gate, origin/dest, time) is always visible with no truncation. Tablet layout unchanged." },
    ],
  },
];
